import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

export const supportedLanguages = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "el", label: "Ελληνικά" },
] as const;

const resources = {
  en: {
    translation: {
      nav: {
        signIn: "Sign In",
        getStarted: "Get Started",
        language: "Language",
      },
      alerts: {
        downloadPng: "Downloading QR code as PNG...",
        downloadSvg: "Downloading QR code as SVG...",
        resendEmail: "Verification email resent!",
      },
      directory: {
        hero: {
          badge: "Trusted by 500+ hotels worldwide",
          titlePart1: "Elevate Guest Experience with",
          titleHighlight: "the most intelligent AI Concierge",
          description:
            "Upload your hotel directory and instantly turn it into a modern digital concierge your guests can access with a single QR scan.",
          bullet1: "No credit card required",
          bullet2: "Setup in under 2 minutes",
          bullet3: "Free to try",
        },
        steps: {
          heading: "Get Started in 2 Simple Steps",
          subheading:
            "Upload your hotel guest directory and get a QR code ready to print and share",
        },
        upload: {
          title: "Upload your hotel guest directory",
          dropTitle: "Drag and drop your guest directory here",
          dropSubtitle: "or click to browse from your computer",
          button: "Choose File",
          formats: "Supported formats: PDF, DOC, DOCX (Max 10MB)",
          fileSize: "{{size}} KB",
        },
        download: {
          title: "Download your QR code",
          info: "We'll process your directory and generate a professional QR code ready to print and share with guests",
        },
        actions: {
          submit: "Process Directory & Generate QR Code",
        },
        features: {
          title: "Why Choose Conciergie?",
          subtitle:
            "Join hundreds of hotels providing seamless digital experiences to their guests",
          cards: {
            instant: {
              title: "Instant Setup",
              description:
                "Upload your directory and generate a professional QR code in under 2 minutes. No technical skills required.",
            },
            secure: {
              title: "Secure & Reliable",
              description:
                "Enterprise-grade security ensures your guest information is always protected and accessible.",
            },
            access: {
              title: "24/7 Access",
              description:
                "Guests can access your directory anytime, anywhere. Update information in real-time.",
            },
          },
        },
      },
      processing: {
        title: "Analyzing your hotel information",
        description:
          "We are processing your documents to extract services, menus, and hotel rules.",
        duration: "This usually takes under a minute.",
        progress: "{{value}}% complete",
        steps: {
          upload: "Uploading documents",
          extract: "Extracting hotel information",
          analyze: "Analyzing services and amenities",
          finalize: "Finalizing setup",
        },
        note: "You can safely stay on this page while we work. We'll automatically proceed when complete.",
        back: "Back",
      },
      qr: {
        back: "Back to upload",
        title: "Your QR Code is Ready!",
        subtitle:
          "Download, print, or share your digital concierge QR code with guests",
        preview: {
          title: "QR Code Preview",
          caption: "Scan to view guest directory",
          note: "This QR code links to your digital guest directory",
        },
        download: {
          title: "Download Options",
          png: "Download as PNG",
          svg: "Download as SVG",
          print: "Print QR Code",
        },
        share: {
          title: "Share Options",
          copy: "Copy shareable link",
        },
        tips: {
          title: "Printing Tips",
          items: [
            "Minimum size: 2x2 inches for reliable scanning",
            "Use high-quality paper for best results",
            "Place in high-traffic areas (lobby, rooms, restaurant)",
            "Test scanning before distributing widely",
          ],
        },
        register: "Register to Have Full Access",
      },
      email: {
        back: "Back",
        form: {
          title: "Register for Full Access",
          subtitle: "Get unlimited QR codes, analytics, and more",
          field: {
            label: "Email Address",
            placeholder: "you@hotel.com",
          },
          helper:
            "We'll send you a verification link to continue registration",
          submit: "Continue with Email",
          sending: "Sending...",
          featuresTitle: "Full access includes:",
          features: [
            "Unlimited QR code generation",
            "Real-time analytics and insights",
            "Custom branding options",
            "Multiple property management",
            "Priority support",
          ],
        },
        success: {
          title: "Check Your Email",
          message: "We've sent a verification email to:",
          instructions:
            "Please check your inbox and click on the <strong>\"Continue Registration\"</strong> link to complete your account setup.",
          resendPrompt: "Didn't receive the email?",
          resend: "Resend Email",
          resending: "Sending...",
        },
      },
    },
  },
  es: {
    translation: {
      nav: {
        signIn: "Iniciar sesión",
        getStarted: "Empezar",
        language: "Idioma",
      },
      alerts: {
        downloadPng: "Descargando el código QR en PNG...",
        downloadSvg: "Descargando el código QR en SVG...",
        resendEmail: "¡Correo de verificación reenviado!",
      },
      directory: {
        hero: {
          badge: "Confiado por más de 500 hoteles en todo el mundo",
          titlePart1: "Eleva la experiencia de tus huéspedes con",
          titleHighlight: "el concierge virtual con IA más inteligente",
          description:
            "Sube el directorio de tu hotel y conviértelo al instante en un concierge digital moderno al que tus huéspedes acceden con un solo escaneo QR.",
          bullet1: "No se requiere tarjeta de crédito",
          bullet2: "Configuración en menos de 2 minutos",
          bullet3: "Pruébalo gratis",
        },
        steps: {
          heading: "Empieza en 2 simples pasos",
          subheading:
            "Sube el directorio de tu hotel y obtén un código QR listo para imprimir y compartir",
        },
        upload: {
          title: "Sube el directorio de huéspedes de tu hotel",
          dropTitle: "Arrastra y suelta tu directorio aquí",
          dropSubtitle: "o haz clic para buscar en tu ordenador",
          button: "Elegir archivo",
          formats: "Formatos admitidos: PDF, DOC, DOCX (máx. 10 MB)",
          fileSize: "{{size}} KB",
        },
        download: {
          title: "Descarga tu código QR",
          info: "Procesaremos tu directorio y generaremos un código QR profesional listo para imprimir y compartir con los huéspedes",
        },
        actions: {
          submit: "Procesar directorio y generar código QR",
        },
        features: {
          title: "¿Por qué elegir Conciergie?",
          subtitle:
            "Únete a cientos de hoteles que ofrecen experiencias digitales sin fricciones a sus huéspedes",
          cards: {
            instant: {
              title: "Configuración instantánea",
              description:
                "Sube tu directorio y genera un código QR profesional en menos de 2 minutos. No necesitas conocimientos técnicos.",
            },
            secure: {
              title: "Seguro y fiable",
              description:
                "La seguridad a nivel empresarial garantiza que la información de tus huéspedes siempre esté protegida y disponible.",
            },
            access: {
              title: "Acceso 24/7",
              description:
                "Los huéspedes pueden acceder a tu directorio en cualquier momento y lugar. Actualiza la información en tiempo real.",
            },
          },
        },
      },
      processing: {
        title: "Analizando la información de tu hotel",
        description:
          "Estamos procesando tus documentos para extraer servicios, menús y normas del hotel.",
        duration: "Suele tardar menos de un minuto.",
        progress: "{{value}} % completado",
        steps: {
          upload: "Subiendo documentos",
          extract: "Extrayendo información del hotel",
          analyze: "Analizando servicios y comodidades",
          finalize: "Finalizando la configuración",
        },
        note: "Puedes permanecer en esta página mientras trabajamos. Continuaremos automáticamente al finalizar.",
        back: "Atrás",
      },
      qr: {
        back: "Volver a la carga",
        title: "¡Tu código QR está listo!",
        subtitle:
          "Descarga, imprime o comparte tu código QR de concierge digital con los huéspedes",
        preview: {
          title: "Vista previa del código QR",
          caption: "Escanea para ver el directorio de huéspedes",
          note: "Este código QR enlaza con tu directorio digital",
        },
        download: {
          title: "Opciones de descarga",
          png: "Descargar en PNG",
          svg: "Descargar en SVG",
          print: "Imprimir código QR",
        },
        share: {
          title: "Opciones para compartir",
          copy: "Copiar enlace compartible",
        },
        tips: {
          title: "Consejos de impresión",
          items: [
            "Tamaño mínimo: 2x2 pulgadas para un escaneo fiable",
            "Utiliza papel de alta calidad para mejores resultados",
            "Colócalo en zonas de alto tráfico (recepción, habitaciones, restaurante)",
            "Prueba el escaneo antes de distribuirlo ampliamente",
          ],
        },
        register: "Regístrate para obtener acceso completo",
      },
      email: {
        back: "Atrás",
        form: {
          title: "Regístrate para obtener acceso completo",
          subtitle: "Obtén códigos QR ilimitados, analíticas y más",
          field: {
            label: "Dirección de correo",
            placeholder: "tu@hotel.com",
          },
          helper:
            "Te enviaremos un enlace de verificación para continuar con el registro",
          submit: "Continuar con el correo",
          sending: "Enviando...",
          featuresTitle: "El acceso completo incluye:",
          features: [
            "Generación ilimitada de códigos QR",
            "Analíticas e información en tiempo real",
            "Opciones de personalización de marca",
            "Gestión de múltiples propiedades",
            "Soporte prioritario",
          ],
        },
        success: {
          title: "Revisa tu correo",
          message: "Hemos enviado un correo de verificación a:",
          instructions:
            "Revisa tu bandeja de entrada y haz clic en el enlace <strong>\"Continuar registro\"</strong> para completar la configuración de tu cuenta.",
          resendPrompt: "¿No recibiste el correo?",
          resend: "Reenviar correo",
          resending: "Enviando...",
        },
      },
    },
  },
  fr: {
    translation: {
      nav: {
        signIn: "Se connecter",
        getStarted: "Commencer",
        language: "Langue",
      },
      alerts: {
        downloadPng: "Téléchargement du code QR en PNG...",
        downloadSvg: "Téléchargement du code QR en SVG...",
        resendEmail: "E-mail de vérification renvoyé !",
      },
      directory: {
        hero: {
          badge: "Plus de 500 hôtels nous font confiance dans le monde",
          titlePart1: "Offrez une expérience d'exception à vos clients avec",
          titleHighlight: "le concierge IA le plus intelligent",
          description:
            "Téléchargez le livret d'accueil de votre hôtel et transformez-le instantanément en concierge digital moderne accessible en un seul scan QR.",
          bullet1: "Aucune carte bancaire requise",
          bullet2: "Configuration en moins de 2 minutes",
          bullet3: "Essai gratuit",
        },
        steps: {
          heading: "Démarrez en 2 étapes simples",
          subheading:
            "Téléchargez le livret d'accueil de votre hôtel et obtenez un code QR prêt à imprimer et à partager",
        },
        upload: {
          title: "Téléchargez le livret d'accueil de votre hôtel",
          dropTitle: "Glissez-déposez votre livret ici",
          dropSubtitle:
            "ou cliquez pour le sélectionner sur votre ordinateur",
          button: "Choisir un fichier",
          formats: "Formats pris en charge : PDF, DOC, DOCX (max. 10 Mo)",
          fileSize: "{{size}} Ko",
        },
        download: {
          title: "Téléchargez votre code QR",
          info: "Nous traiterons votre livret et générerons un code QR professionnel prêt à imprimer et à partager avec vos clients",
        },
        actions: {
          submit: "Traiter le livret et générer le code QR",
        },
        features: {
          title: "Pourquoi choisir Conciergie ?",
          subtitle:
            "Rejoignez des centaines d'hôtels offrant des expériences digitales fluides à leurs clients",
          cards: {
            instant: {
              title: "Mise en place instantanée",
              description:
                "Téléchargez votre livret et générez un code QR professionnel en moins de 2 minutes. Aucune compétence technique requise.",
            },
            secure: {
              title: "Sécurisé et fiable",
              description:
                "Une sécurité de niveau entreprise garantit que les informations de vos clients sont toujours protégées et accessibles.",
            },
            access: {
              title: "Accès 24/7",
              description:
                "Vos clients accèdent à votre livret à tout moment, où qu'ils soient. Mettez les informations à jour en temps réel.",
            },
          },
        },
      },
      processing: {
        title: "Analyse des informations de votre hôtel",
        description:
          "Nous traitons vos documents pour extraire les services, menus et règles de l'hôtel.",
        duration: "Cela prend généralement moins d'une minute.",
        progress: "{{value}} % terminé",
        steps: {
          upload: "Téléchargement des documents",
          extract: "Extraction des informations de l'hôtel",
          analyze: "Analyse des services et équipements",
          finalize: "Finalisation de la configuration",
        },
        note: "Vous pouvez rester sur cette page pendant que nous travaillons. Nous poursuivrons automatiquement une fois terminé.",
        back: "Retour",
      },
      qr: {
        back: "Retour au téléversement",
        title: "Votre code QR est prêt !",
        subtitle:
          "Téléchargez, imprimez ou partagez votre code QR de concierge digital avec vos clients",
        preview: {
          title: "Aperçu du code QR",
          caption: "Scannez pour consulter le livret d'accueil",
          note: "Ce code QR renvoie vers votre livret d'accueil digital",
        },
        download: {
          title: "Options de téléchargement",
          png: "Télécharger en PNG",
          svg: "Télécharger en SVG",
          print: "Imprimer le code QR",
        },
        share: {
          title: "Options de partage",
          copy: "Copier le lien partageable",
        },
        tips: {
          title: "Conseils d'impression",
          items: [
            "Taille minimale : 5x5 cm pour un scan fiable",
            "Utilisez un papier de haute qualité pour de meilleurs résultats",
            "Placez-le dans les zones à fort passage (réception, chambres, restaurant)",
            "Testez le scan avant une diffusion large",
          ],
        },
        register: "Inscrivez-vous pour un accès complet",
      },
      email: {
        back: "Retour",
        form: {
          title: "Inscrivez-vous pour un accès complet",
          subtitle: "Obtenez des codes QR illimités, des analyses et plus encore",
          field: {
            label: "Adresse e-mail",
            placeholder: "vous@hotel.com",
          },
          helper:
            "Nous vous enverrons un lien de vérification pour poursuivre votre inscription",
          submit: "Continuer avec l'e-mail",
          sending: "Envoi...",
          featuresTitle: "L'accès complet inclut :",
          features: [
            "Génération illimitée de codes QR",
            "Analyses et insights en temps réel",
            "Options de personnalisation de la marque",
            "Gestion multi-établissements",
            "Support prioritaire",
          ],
        },
        success: {
          title: "Vérifiez votre e-mail",
          message: "Nous avons envoyé un e-mail de vérification à :",
          instructions:
            "Veuillez consulter votre boîte de réception et cliquer sur le lien <strong>\"Poursuivre l'inscription\"</strong> pour finaliser la création de votre compte.",
          resendPrompt: "Vous n'avez pas reçu l'e-mail ?",
          resend: "Renvoyer l'e-mail",
          resending: "Envoi...",
        },
      },
    },
  },
  de: {
    translation: {
      nav: {
        signIn: "Anmelden",
        getStarted: "Jetzt starten",
        language: "Sprache",
      },
      alerts: {
        downloadPng: "QR-Code wird als PNG heruntergeladen...",
        downloadSvg: "QR-Code wird als SVG heruntergeladen...",
        resendEmail: "Bestätigungs-E-Mail erneut gesendet!",
      },
      directory: {
        hero: {
          badge: "Weltweit vertrauen uns über 500 Hotels",
          titlePart1: "Verbessern Sie das Gästeerlebnis mit",
          titleHighlight: "dem intelligentesten KI-Concierge",
          description:
            "Laden Sie Ihr Hoteldirectory hoch und verwandeln Sie es sofort in einen modernen digitalen Concierge, auf den Ihre Gäste mit einem einzigen QR-Scan zugreifen.",
          bullet1: "Keine Kreditkarte erforderlich",
          bullet2: "In weniger als 2 Minuten eingerichtet",
          bullet3: "Kostenlos testen",
        },
        steps: {
          heading: "In 2 einfachen Schritten starten",
          subheading:
            "Laden Sie das Gästebuch Ihres Hotels hoch und erhalten Sie einen QR-Code, der druckfertig ist",
        },
        upload: {
          title: "Laden Sie das Gästebuch Ihres Hotels hoch",
          dropTitle: "Ziehen Sie Ihr Verzeichnis hierher und legen Sie es ab",
          dropSubtitle: "oder klicken Sie, um auf Ihrem Computer zu suchen",
          button: "Datei auswählen",
          formats: "Unterstützte Formate: PDF, DOC, DOCX (max. 10 MB)",
          fileSize: "{{size}} KB",
        },
        download: {
          title: "Laden Sie Ihren QR-Code herunter",
          info: "Wir verarbeiten Ihr Verzeichnis und erstellen einen professionellen QR-Code, der zum Drucken und Teilen mit Gästen bereit ist",
        },
        actions: {
          submit: "Verzeichnis verarbeiten und QR-Code erstellen",
        },
        features: {
          title: "Warum Conciergie wählen?",
          subtitle:
            "Schließen Sie sich Hunderten von Hotels an, die ihren Gästen nahtlose digitale Erlebnisse bieten",
          cards: {
            instant: {
              title: "Sofortige Einrichtung",
              description:
                "Laden Sie Ihr Verzeichnis hoch und generieren Sie in weniger als 2 Minuten einen professionellen QR-Code. Keine technischen Kenntnisse erforderlich.",
            },
            secure: {
              title: "Sicher & zuverlässig",
              description:
                "Sicherheit auf Enterprise-Niveau stellt sicher, dass die Informationen Ihrer Gäste immer geschützt und verfügbar sind.",
            },
            access: {
              title: "Zugang rund um die Uhr",
              description:
                "Gäste können jederzeit und überall auf Ihr Verzeichnis zugreifen. Aktualisieren Sie Informationen in Echtzeit.",
            },
          },
        },
      },
      processing: {
        title: "Analyse Ihrer Hotelinformationen",
        description:
          "Wir verarbeiten Ihre Dokumente, um Services, Speisekarten und Hotelregeln zu extrahieren.",
        duration: "Das dauert in der Regel weniger als eine Minute.",
        progress: "{{value}} % abgeschlossen",
        steps: {
          upload: "Dokumente werden hochgeladen",
          extract: "Hotelinformationen werden extrahiert",
          analyze: "Services und Annehmlichkeiten werden analysiert",
          finalize: "Einrichtung wird abgeschlossen",
        },
        note: "Sie können auf dieser Seite bleiben, während wir arbeiten. Nach Abschluss fahren wir automatisch fort.",
        back: "Zurück",
      },
      qr: {
        back: "Zurück zum Upload",
        title: "Ihr QR-Code ist bereit!",
        subtitle:
          "Laden Sie Ihren digitalen Concierge-QR-Code herunter, drucken oder teilen Sie ihn mit Gästen",
        preview: {
          title: "QR-Code-Vorschau",
          caption: "Scannen, um das Gästebuch anzuzeigen",
          note: "Dieser QR-Code führt zu Ihrem digitalen Gästebuch",
        },
        download: {
          title: "Download-Optionen",
          png: "Als PNG herunterladen",
          svg: "Als SVG herunterladen",
          print: "QR-Code drucken",
        },
        share: {
          title: "Freigabeoptionen",
          copy: "Teilbaren Link kopieren",
        },
        tips: {
          title: "Tipps zum Drucken",
          items: [
            "Mindestgröße: 5x5 cm für zuverlässiges Scannen",
            "Verwenden Sie hochwertiges Papier für beste Ergebnisse",
            "Platzieren Sie ihn in stark frequentierten Bereichen (Lobby, Zimmer, Restaurant)",
            "Testen Sie das Scannen, bevor Sie weit verbreiten",
          ],
        },
        register: "Registrieren, um vollen Zugriff zu erhalten",
      },
      email: {
        back: "Zurück",
        form: {
          title: "Für vollen Zugriff registrieren",
          subtitle: "Erhalten Sie unbegrenzte QR-Codes, Analysen und mehr",
          field: {
            label: "E-Mail-Adresse",
            placeholder: "sie@hotel.com",
          },
          helper:
            "Wir senden Ihnen einen Bestätigungslink, um die Registrierung fortzusetzen",
          submit: "Mit E-Mail fortfahren",
          sending: "Senden...",
          featuresTitle: "Voller Zugriff umfasst:",
          features: [
            "Unbegrenzte QR-Code-Erstellung",
            "Echtzeitanalysen und Einblicke",
            "Individuelle Branding-Optionen",
            "Verwaltung mehrerer Standorte",
            "Priorisierter Support",
          ],
        },
        success: {
          title: "Überprüfen Sie Ihre E-Mail",
          message: "Wir haben eine Bestätigungs-E-Mail gesendet an:",
          instructions:
            "Bitte prüfen Sie Ihren Posteingang und klicken Sie auf den Link <strong>\"Registrierung fortsetzen\"</strong>, um die Einrichtung Ihres Kontos abzuschließen.",
          resendPrompt: "E-Mail nicht erhalten?",
          resend: "E-Mail erneut senden",
          resending: "Senden...",
        },
      },
    },
  },
  it: {
    translation: {
      nav: {
        signIn: "Accedi",
        getStarted: "Inizia ora",
        language: "Lingua",
      },
      alerts: {
        downloadPng: "Download del codice QR in PNG in corso...",
        downloadSvg: "Download del codice QR in SVG in corso...",
        resendEmail: "E-mail di verifica reinviata!",
      },
      directory: {
        hero: {
          badge: "Oltre 500 hotel in tutto il mondo si affidano a noi",
          titlePart1: "Eleva l'esperienza degli ospiti con",
          titleHighlight: "il concierge IA più intelligente",
          description:
            "Carica l'annuario del tuo hotel e trasformalo all'istante in un concierge digitale moderno a cui gli ospiti accedono con una sola scansione QR.",
          bullet1: "Nessuna carta di credito richiesta",
          bullet2: "Configurazione in meno di 2 minuti",
          bullet3: "Provalo gratis",
        },
        steps: {
          heading: "Inizia in 2 semplici passaggi",
          subheading:
            "Carica l'annuario del tuo hotel e ottieni un codice QR pronto da stampare e condividere",
        },
        upload: {
          title: "Carica l'annuario degli ospiti del tuo hotel",
          dropTitle: "Trascina qui il tuo annuario",
          dropSubtitle: "oppure clicca per cercare dal tuo computer",
          button: "Scegli file",
          formats: "Formati supportati: PDF, DOC, DOCX (max 10 MB)",
          fileSize: "{{size}} KB",
        },
        download: {
          title: "Scarica il tuo codice QR",
          info: "Elaboreremo il tuo annuario e genereremo un codice QR professionale pronto da stampare e condividere con gli ospiti",
        },
        actions: {
          submit: "Elabora annuario e genera codice QR",
        },
        features: {
          title: "Perché scegliere Conciergie?",
          subtitle:
            "Unisciti a centinaia di hotel che offrono esperienze digitali senza attriti ai loro ospiti",
          cards: {
            instant: {
              title: "Configurazione immediata",
              description:
                "Carica il tuo annuario e genera un codice QR professionale in meno di 2 minuti. Non sono richieste competenze tecniche.",
            },
            secure: {
              title: "Sicuro e affidabile",
              description:
                "La sicurezza di livello enterprise garantisce che le informazioni dei tuoi ospiti siano sempre protette e accessibili.",
            },
            access: {
              title: "Accesso 24/7",
              description:
                "Gli ospiti possono accedere al tuo annuario in qualsiasi momento e ovunque. Aggiorna le informazioni in tempo reale.",
            },
          },
        },
      },
      processing: {
        title: "Analizziamo le informazioni del tuo hotel",
        description:
          "Stiamo elaborando i tuoi documenti per estrarre servizi, menù e regole dell'hotel.",
        duration: "Di solito richiede meno di un minuto.",
        progress: "{{value}}% completato",
        steps: {
          upload: "Caricamento dei documenti",
          extract: "Estrazione delle informazioni dell'hotel",
          analyze: "Analisi di servizi e comfort",
          finalize: "Finalizzazione della configurazione",
        },
        note: "Puoi rimanere su questa pagina mentre lavoriamo. Procederemo automaticamente al termine.",
        back: "Indietro",
      },
      qr: {
        back: "Torna al caricamento",
        title: "Il tuo codice QR è pronto!",
        subtitle:
          "Scarica, stampa o condividi il tuo codice QR del concierge digitale con gli ospiti",
        preview: {
          title: "Anteprima del codice QR",
          caption: "Scansiona per visualizzare l'annuario degli ospiti",
          note: "Questo codice QR rimanda al tuo annuario digitale",
        },
        download: {
          title: "Opzioni di download",
          png: "Scarica in PNG",
          svg: "Scarica in SVG",
          print: "Stampa il codice QR",
        },
        share: {
          title: "Opzioni di condivisione",
          copy: "Copia link condivisibile",
        },
        tips: {
          title: "Suggerimenti per la stampa",
          items: [
            "Dimensione minima: 5x5 cm per una scansione affidabile",
            "Usa carta di alta qualità per risultati migliori",
            "Posizionalo nelle aree ad alto traffico (hall, camere, ristorante)",
            "Esegui un test di scansione prima di distribuirlo ampiamente",
          ],
        },
        register: "Registrati per l'accesso completo",
      },
      email: {
        back: "Indietro",
        form: {
          title: "Registrati per l'accesso completo",
          subtitle: "Ottieni codici QR illimitati, analisi e altro ancora",
          field: {
            label: "Indirizzo e-mail",
            placeholder: "tuo@hotel.com",
          },
          helper:
            "Ti invieremo un link di verifica per proseguire con la registrazione",
          submit: "Continua con l'e-mail",
          sending: "Invio...",
          featuresTitle: "L'accesso completo include:",
          features: [
            "Generazione illimitata di codici QR",
            "Analisi e insight in tempo reale",
            "Opzioni di personalizzazione del branding",
            "Gestione di più strutture",
            "Supporto prioritario",
          ],
        },
        success: {
          title: "Controlla la tua e-mail",
          message: "Abbiamo inviato un'e-mail di verifica a:",
          instructions:
            "Controlla la tua casella di posta e fai clic sul link <strong>\"Continua registrazione\"</strong> per completare la configurazione del tuo account.",
          resendPrompt: "Non hai ricevuto l'e-mail?",
          resend: "Invia di nuovo l'e-mail",
          resending: "Invio...",
        },
      },
    },
  },
  pt: {
    translation: {
      nav: {
        signIn: "Iniciar sessão",
        getStarted: "Começar",
        language: "Idioma",
      },
      alerts: {
        downloadPng: "A descarregar o código QR em PNG...",
        downloadSvg: "A descarregar o código QR em SVG...",
        resendEmail: "E-mail de verificação reenviado!",
      },
      directory: {
        hero: {
          badge: "Mais de 500 hotéis em todo o mundo confiam em nós",
          titlePart1: "Melhore a experiência dos hóspedes com",
          titleHighlight: "o concierge com IA mais inteligente",
          description:
            "Carregue o diretório do seu hotel e transforme-o instantaneamente num concierge digital moderno a que os hóspedes acedem com apenas uma leitura QR.",
          bullet1: "Não é necessário cartão de crédito",
          bullet2: "Configuração em menos de 2 minutos",
          bullet3: "Experimente gratuitamente",
        },
        steps: {
          heading: "Comece em 2 passos simples",
          subheading:
            "Carregue o diretório do seu hotel e receba um código QR pronto a imprimir e partilhar",
        },
        upload: {
          title: "Carregue o diretório de hóspedes do seu hotel",
          dropTitle: "Arraste e largue o seu diretório aqui",
          dropSubtitle: "ou clique para procurar no seu computador",
          button: "Escolher ficheiro",
          formats: "Formatos suportados: PDF, DOC, DOCX (máx. 10 MB)",
          fileSize: "{{size}} KB",
        },
        download: {
          title: "Descarregue o seu código QR",
          info: "Vamos processar o seu diretório e gerar um código QR profissional pronto a imprimir e partilhar com os hóspedes",
        },
        actions: {
          submit: "Processar diretório e gerar código QR",
        },
        features: {
          title: "Porquê escolher a Conciergie?",
          subtitle:
            "Junte-se a centenas de hotéis que oferecem experiências digitais sem atritos aos seus hóspedes",
          cards: {
            instant: {
              title: "Configuração imediata",
              description:
                "Carregue o seu diretório e gere um código QR profissional em menos de 2 minutos. Não são necessárias competências técnicas.",
            },
            secure: {
              title: "Seguro e fiável",
              description:
                "Segurança de nível empresarial garante que a informação dos seus hóspedes está sempre protegida e acessível.",
            },
            access: {
              title: "Acesso 24/7",
              description:
                "Os hóspedes podem aceder ao seu diretório em qualquer altura e lugar. Atualize as informações em tempo real.",
            },
          },
        },
      },
      processing: {
        title: "A analisar a informação do seu hotel",
        description:
          "Estamos a processar os seus documentos para extrair serviços, menus e regras do hotel.",
        duration: "Normalmente demora menos de um minuto.",
        progress: "{{value}} % concluído",
        steps: {
          upload: "A carregar documentos",
          extract: "A extrair informação do hotel",
          analyze: "A analisar serviços e comodidades",
          finalize: "A finalizar a configuração",
        },
        note: "Pode permanecer nesta página enquanto trabalhamos. Avançaremos automaticamente quando estiver concluído.",
        back: "Voltar",
      },
      qr: {
        back: "Voltar ao carregamento",
        title: "O seu código QR está pronto!",
        subtitle:
          "Descarregue, imprima ou partilhe o seu código QR do concierge digital com os hóspedes",
        preview: {
          title: "Pré-visualização do código QR",
          caption: "Leia para ver o diretório de hóspedes",
          note: "Este código QR liga ao seu diretório digital",
        },
        download: {
          title: "Opções de descarga",
          png: "Descarregar em PNG",
          svg: "Descarregar em SVG",
          print: "Imprimir código QR",
        },
        share: {
          title: "Opções de partilha",
          copy: "Copiar ligação partilhável",
        },
        tips: {
          title: "Dicas de impressão",
          items: [
            "Tamanho mínimo: 5x5 cm para uma leitura fiável",
            "Use papel de alta qualidade para melhores resultados",
            "Coloque-o em zonas de grande passagem (receção, quartos, restaurante)",
            "Teste a leitura antes de o distribuir amplamente",
          ],
        },
        register: "Registe-se para obter acesso total",
      },
      email: {
        back: "Voltar",
        form: {
          title: "Registe-se para obter acesso total",
          subtitle: "Obtenha códigos QR ilimitados, análises e muito mais",
          field: {
            label: "Endereço de e-mail",
            placeholder: "voce@hotel.com",
          },
          helper:
            "Enviar-lhe-emos uma ligação de verificação para continuar o registo",
          submit: "Continuar com o e-mail",
          sending: "A enviar...",
          featuresTitle: "O acesso total inclui:",
          features: [
            "Geração ilimitada de códigos QR",
            "Análises e insights em tempo real",
            "Opções de personalização da marca",
            "Gestão de várias propriedades",
            "Suporte prioritário",
          ],
        },
        success: {
          title: "Verifique o seu e-mail",
          message: "Enviámos um e-mail de verificação para:",
          instructions:
            "Verifique a sua caixa de entrada e clique na ligação <strong>\"Continuar registo\"</strong> para concluir a configuração da conta.",
          resendPrompt: "Não recebeu o e-mail?",
          resend: "Reenviar e-mail",
          resending: "A enviar...",
        },
      },
    },
  },
  nl: {
    translation: {
      nav: {
        signIn: "Inloggen",
        getStarted: "Aan de slag",
        language: "Taal",
      },
      alerts: {
        downloadPng: "QR-code wordt als PNG gedownload...",
        downloadSvg: "QR-code wordt als SVG gedownload...",
        resendEmail: "Verificatiemail opnieuw verzonden!",
      },
      directory: {
        hero: {
          badge: "Vertrouwd door meer dan 500 hotels wereldwijd",
          titlePart1: "Til de gastervaring naar een hoger niveau met",
          titleHighlight: "de slimste AI-conciërge",
          description:
            "Upload de hotelgids en verander deze direct in een moderne digitale conciërge waar gasten met één QR-scan toegang toe hebben.",
          bullet1: "Geen creditcard nodig",
          bullet2: "Binnen 2 minuten ingesteld",
          bullet3: "Gratis uitproberen",
        },
        steps: {
          heading: "Begin in 2 eenvoudige stappen",
          subheading:
            "Upload de hotelgids en ontvang een QR-code die klaar is om te printen en te delen",
        },
        upload: {
          title: "Upload de hotelgids voor uw gasten",
          dropTitle: "Sleep uw gids hierheen",
          dropSubtitle: "of klik om op uw computer te zoeken",
          button: "Bestand kiezen",
          formats: "Ondersteunde formaten: PDF, DOC, DOCX (max. 10 MB)",
          fileSize: "{{size}} KB",
        },
        download: {
          title: "Download uw QR-code",
          info: "Wij verwerken uw gids en genereren een professionele QR-code die klaar is om te printen en met gasten te delen",
        },
        actions: {
          submit: "Gids verwerken en QR-code genereren",
        },
        features: {
          title: "Waarom kiezen voor Conciergie?",
          subtitle:
            "Sluit u aan bij honderden hotels die hun gasten naadloze digitale ervaringen bieden",
          cards: {
            instant: {
              title: "Direct klaar",
              description:
                "Upload uw gids en genereer binnen 2 minuten een professionele QR-code. Geen technische kennis nodig.",
            },
            secure: {
              title: "Veilig en betrouwbaar",
              description:
                "Beveiliging op ondernemingsniveau zorgt ervoor dat de gegevens van uw gasten altijd beschermd en beschikbaar zijn.",
            },
            access: {
              title: "24/7 toegang",
              description:
                "Gasten hebben altijd en overal toegang tot uw gids. Werk informatie in real-time bij.",
            },
          },
        },
      },
      processing: {
        title: "We analyseren uw hotelinformatie",
        description:
          "We verwerken uw documenten om diensten, menukaarten en hotelregels te extraheren.",
        duration: "Dit duurt meestal minder dan een minuut.",
        progress: "{{value}}% voltooid",
        steps: {
          upload: "Documenten worden geüpload",
          extract: "Hotelinformatie wordt geëxtraheerd",
          analyze: "Diensten en voorzieningen worden geanalyseerd",
          finalize: "Configuratie wordt afgerond",
        },
        note: "U kunt op deze pagina blijven terwijl we werken. We gaan automatisch verder zodra alles klaar is.",
        back: "Terug",
      },
      qr: {
        back: "Terug naar uploaden",
        title: "Uw QR-code is klaar!",
        subtitle:
          "Download, print of deel uw digitale conciërge-QR-code met gasten",
        preview: {
          title: "QR-code voorbeeld",
          caption: "Scan om de hotelgids te bekijken",
          note: "Deze QR-code verwijst naar uw digitale hotelgids",
        },
        download: {
          title: "Downloadopties",
          png: "Downloaden als PNG",
          svg: "Downloaden als SVG",
          print: "QR-code afdrukken",
        },
        share: {
          title: "Deelopties",
          copy: "Deelbare link kopiëren",
        },
        tips: {
          title: "Printtips",
          items: [
            "Minimale afmeting: 5x5 cm voor betrouwbaar scannen",
            "Gebruik papier van hoge kwaliteit voor het beste resultaat",
            "Plaats hem op drukbezochte plekken (lobby, kamers, restaurant)",
            "Test de scan voordat u breed verspreidt",
          ],
        },
        register: "Registreer voor volledige toegang",
      },
      email: {
        back: "Terug",
        form: {
          title: "Registreer voor volledige toegang",
          subtitle: "Ontvang onbeperkte QR-codes, analyses en meer",
          field: {
            label: "E-mailadres",
            placeholder: "jij@hotel.com",
          },
          helper:
            "We sturen u een verificatielink om de registratie voort te zetten",
          submit: "Doorgaan met e-mail",
          sending: "Versturen...",
          featuresTitle: "Volledige toegang omvat:",
          features: [
            "Onbeperkte QR-codegeneratie",
            "Realtime analyses en inzichten",
            "Mogelijkheden voor merkpersonalisatie",
            "Beheer van meerdere locaties",
            "Prioritaire ondersteuning",
          ],
        },
        success: {
          title: "Controleer uw e-mail",
          message: "We hebben een verificatiemail gestuurd naar:",
          instructions:
            "Controleer uw inbox en klik op de link <strong>\"Registratie vervolgen\"</strong> om uw account in te stellen.",
          resendPrompt: "E-mail niet ontvangen?",
          resend: "E-mail opnieuw verzenden",
          resending: "Versturen...",
        },
      },
    },
  },
  el: {
    translation: {
      nav: {
        signIn: "Σύνδεση",
        getStarted: "Ξεκινήστε",
        language: "Γλώσσα",
      },
      alerts: {
        downloadPng: "Γίνεται λήψη του κωδικού QR σε μορφή PNG...",
        downloadSvg: "Γίνεται λήψη του κωδικού QR σε μορφή SVG...",
        resendEmail: "Το email επιβεβαίωσης στάλθηκε ξανά!",
      },
      directory: {
        hero: {
          badge: "Εμπιστεύονται πάνω από 500 ξενοδοχεία παγκοσμίως",
          titlePart1: "Αναβαθμίστε την εμπειρία των επισκεπτών με",
          titleHighlight: "τον πιο έξυπνο concierge με ΤΝ",
          description:
            "Ανεβάστε τον οδηγό του ξενοδοχείου σας και μετατρέψτε τον άμεσα σε ένα σύγχρονο ψηφιακό concierge που οι επισκέπτες σας μπορούν να προσπελάσουν με ένα μόνο σκανάρισμα QR.",
          bullet1: "Δεν απαιτείται πιστωτική κάρτα",
          bullet2: "Ρύθμιση σε λιγότερο από 2 λεπτά",
          bullet3: "Δοκιμή δωρεάν",
        },
        steps: {
          heading: "Ξεκινήστε σε 2 απλά βήματα",
          subheading:
            "Ανεβάστε τον οδηγό επισκεπτών του ξενοδοχείου σας και αποκτήστε έναν κωδικό QR έτοιμο για εκτύπωση και κοινοποίηση",
        },
        upload: {
          title: "Ανεβάστε τον οδηγό επισκεπτών του ξενοδοχείου σας",
          dropTitle: "Σύρετε και αφήστε τον οδηγό σας εδώ",
          dropSubtitle: "ή κάντε κλικ για να αναζητήσετε στον υπολογιστή σας",
          button: "Επιλογή αρχείου",
          formats: "Υποστηριζόμενες μορφές: PDF, DOC, DOCX (μέγιστο 10 MB)",
          fileSize: "{{size}} KB",
        },
        download: {
          title: "Κατεβάστε τον κωδικό QR σας",
          info: "Θα επεξεργαστούμε τον οδηγό σας και θα δημιουργήσουμε έναν επαγγελματικό κωδικό QR έτοιμο για εκτύπωση και κοινοποίηση στους επισκέπτες",
        },
        actions: {
          submit: "Επεξεργασία οδηγού & δημιουργία κωδικού QR",
        },
        features: {
          title: "Γιατί να επιλέξετε το Conciergie;",
          subtitle:
            "Ενταχθείτε σε εκατοντάδες ξενοδοχεία που προσφέρουν απρόσκοπτες ψηφιακές εμπειρίες στους επισκέπτες τους",
          cards: {
            instant: {
              title: "Άμεση ρύθμιση",
              description:
                "Ανεβάστε τον οδηγό σας και δημιουργήστε έναν επαγγελματικό κωδικό QR σε λιγότερο από 2 λεπτά. Δεν απαιτούνται τεχνικές γνώσεις.",
            },
            secure: {
              title: "Ασφαλές & αξιόπιστο",
              description:
                "Η ασφάλεια επιπέδου enterprise εξασφαλίζει ότι οι πληροφορίες των επισκεπτών σας είναι πάντα προστατευμένες και διαθέσιμες.",
            },
            access: {
              title: "Πρόσβαση 24/7",
              description:
                "Οι επισκέπτες μπορούν να έχουν πρόσβαση στον οδηγό σας οποτεδήποτε και οπουδήποτε. Ενημερώστε τις πληροφορίες σε πραγματικό χρόνο.",
            },
          },
        },
      },
      processing: {
        title: "Αναλύουμε τις πληροφορίες του ξενοδοχείου σας",
        description:
          "Επεξεργαζόμαστε τα έγγραφά σας για να εξάγουμε υπηρεσίες, μενού και κανονισμούς του ξενοδοχείου.",
        duration: "Συνήθως χρειάζεται λιγότερο από ένα λεπτό.",
        progress: "{{value}}% ολοκληρώθηκε",
        steps: {
          upload: "Μεταφόρτωση εγγράφων",
          extract: "Εξαγωγή πληροφοριών του ξενοδοχείου",
          analyze: "Ανάλυση υπηρεσιών και παροχών",
          finalize: "Ολοκλήρωση ρύθμισης",
        },
        note: "Μπορείτε να μείνετε σε αυτή τη σελίδα όσο δουλεύουμε. Θα προχωρήσουμε αυτόματα όταν ολοκληρωθεί.",
        back: "Πίσω",
      },
      qr: {
        back: "Πίσω στη μεταφόρτωση",
        title: "Ο κωδικός QR σας είναι έτοιμος!",
        subtitle:
          "Κατεβάστε, εκτυπώστε ή κοινοποιήστε τον κωδικό QR του ψηφιακού concierge στους επισκέπτες σας",
        preview: {
          title: "Προεπισκόπηση κωδικού QR",
          caption: "Σαρώστε για να δείτε τον οδηγό επισκεπτών",
          note: "Αυτός ο κωδικός QR οδηγεί στον ψηφιακό οδηγό σας",
        },
        download: {
          title: "Επιλογές λήψης",
          png: "Λήψη ως PNG",
          svg: "Λήψη ως SVG",
          print: "Εκτύπωση κωδικού QR",
        },
        share: {
          title: "Επιλογές κοινοποίησης",
          copy: "Αντιγραφή κοινοποιήσιμου συνδέσμου",
        },
        tips: {
          title: "Συμβουλές εκτύπωσης",
          items: [
            "Ελάχιστο μέγεθος: 5x5 εκ. για αξιόπιστη σάρωση",
            "Χρησιμοποιήστε χαρτί υψηλής ποιότητας για καλύτερα αποτελέσματα",
            "Τοποθετήστε τον σε σημεία με μεγάλη επισκεψιμότητα (λόμπι, δωμάτια, εστιατόριο)",
            "Δοκιμάστε τη σάρωση πριν τον διανείμετε ευρέως",
          ],
        },
        register: "Εγγραφείτε για πλήρη πρόσβαση",
      },
      email: {
        back: "Πίσω",
        form: {
          title: "Εγγραφείτε για πλήρη πρόσβαση",
          subtitle: "Αποκτήστε απεριόριστους κωδικούς QR, αναλύσεις και πολλά άλλα",
          field: {
            label: "Διεύθυνση email",
            placeholder: "eseis@hotel.com",
          },
          helper:
            "Θα σας στείλουμε έναν σύνδεσμο επιβεβαίωσης για να συνεχίσετε την εγγραφή",
          submit: "Συνέχεια με email",
          sending: "Αποστολή...",
          featuresTitle: "Η πλήρης πρόσβαση περιλαμβάνει:",
          features: [
            "Απεριόριστη δημιουργία κωδικών QR",
            "Αναλύσεις και πληροφορίες σε πραγματικό χρόνο",
            "Επιλογές εξατομίκευσης της επωνυμίας",
            "Διαχείριση πολλαπλών καταλυμάτων",
            "Υποστήριξη προτεραιότητας",
          ],
        },
        success: {
          title: "Ελέγξτε το email σας",
          message: "Στείλαμε email επιβεβαίωσης στη διεύθυνση:",
          instructions:
            "Ελέγξτε τα εισερχόμενά σας και κάντε κλικ στον σύνδεσμο <strong>\"Συνέχεια εγγραφής\"</strong> για να ολοκληρώσετε τη ρύθμιση του λογαριασμού σας.",
          resendPrompt: "Δεν λάβατε το email;",
          resend: "Επαναποστολή email",
          resending: "Αποστολή...",
        },
      },
    },
  },
} as const;

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: "en",
      supportedLngs: supportedLanguages.map((lang) => lang.code),
      detection: {
        order: ["localStorage", "navigator", "htmlTag"],
        caches: ["localStorage"],
      },
      interpolation: {
        escapeValue: false,
      },
    });
}

export default i18n;

